import { CategoryFormSettings, Contract, Milestone, User } from "../modules";
import { Currency } from "../modules/professional/models/ProfessionalInterface";
import { HttpService } from "../modules/shared/services/http.service";
import { convertCurrency } from "../lib/currencyConversion";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

// Escrow Status Enum
export enum EscrowStatusEnum {
	"pending" = 1,
	"funded" = 2,
	"released" = 3,
	"disputed" = 4,
	"resolved" = 5,
	"refunded" = 6,
	"cancelled" = 7,
	"failed" = 8,
	"withdrawn" = 9,
	"request_withdraw" = 10,
	"user_funded" = 11,
	"credits_purchased" = 12,
	"quote_purchased" = 13,
	"used_credit_purchased" = 14,
	"used_quote_purchased" = 15,
	"used_funds" = 16,
}

// Clearing Status Enum
export enum ClearingStatusEnum {
	"pending_clearing" = 1,
	"manually_released" = 2,
	"admin_held" = 3,
	"auto_cleared" = 4,
}

// Keep the old enum for backward compatibility
export enum EscrowStatus {
	PENDING = 1,
	FUNDED = 2,
	RELEASED = 3,
	DISPUTED = 4,
	RESOLVED = 5,
	REFUNDED = 6,
	CANCELLED = 7,
	FAILED = 8,
	WITHDRAWN = 9,
	REQUEST_WITHDRAW = 10,
	USER_FUNDED = 11,
	CREDITS_PURCHASED = 12,
	QUOTE_PURCHASED = 13,
	USED_CREDIT_PURCHASED = 14,
	USED_QUOTE_PURCHASED = 15,
	USED_FUNDS = 16,
}

// Dispute Reason Enum
export enum DisputeReason {
	WORK_NOT_COMPLETED = 1,
	WORK_NOT_SATISFACTORY = 2,
	CHANGE_OF_REQUIREMENTS = 3,
	COMMUNICATION_ISSUES = 4,
	PAYMENT_ISSUES = 5,
	OTHER = 6
}

// Payment Provider Enum
export enum EscrowPaymentProvider {
	STRIPE = 1
}

// Main Escrow Interface
export interface Escrow {
	id: string;
	contract?: Contract | null;
	client?: User | null;
	professional?: User | null;
	amount?: number | null; // Milestone amount (base amount)
	emergencyCost?: number | null; // Emergency cost amount
	trackerAmount?: number | null; // Smart Work Tracker amount
	platformFee?: number | null; // Platform fee amount
	totalAmount?: number | null; // Total amount (milestone + emergency + tracker + platform fee)
	withdrawlAmount?: number | null; // Amount after commission deduction
	currency?: Currency | null;
	paymentProvider?: EscrowPaymentProvider | null;

	// Stripe fields
	stripePaymentIntentId?: string | null;
	stripeAccountId?: string | null;
	stripeTransferId?: string | null;
	stripeConnectedAccountId?: string | null;

	// Status and dates
	status?: {
		id: EscrowStatus;
		name?: string;
	} | null;
	clearingStatus?: {
		id: number;
		name?: string;
	} | null;
	clearingReleasedAt?: string | null;
	fundedAt?: string | null;
	expectedReleaseDate?: string | null;
	actualReleaseDate?: string | null; // When payment was released (start of 72h clearing window)
	autoReleaseEnabled?: boolean | null;

	// Dispute fields
	disputeReason?: {
		id: DisputeReason;
		name?: string;
	} | null;
	disputeDescription?: string | null;
	disputeInitiatedAt?: string | null;
	disputeInitiatedBy?: User | null;
	disputeResolutionDate?: string | null;
	disputeResolvedBy?: User | null;

	// Milestone integration
	milestone?: Milestone | null;
	isMilestonePayment?: boolean | null;

	// Metadata
	notes?: string | null;
	metadata?: Record<string, any> | null;

	// Timestamps
	createdAt: string;
	updatedAt: string;
}

// Create Escrow DTO
export interface EscrowCreateDto {
	contractId?: string;
	clientId?: string;
	professionalId?: string;
	amount: number; // Milestone amount (base amount)
	emergencyCost?: number; // Emergency cost amount
	trackerAmount?: number; // Smart Work Tracker amount
	platformFee?: number; // Platform fee amount (2% of total base amount)
	totalAmount?: number; // Total amount (milestone + emergency + tracker + platform fee)
	withdrawlAmount?: number; // Amount after commission deduction
	currency?: string; // Backend expects "currency" field
	autoReleaseEnabled?: boolean;
	milestoneId?: string;
	notes?: string;
	metadata?: Record<string, any>;
	status?: string; // For status updates
}

// Update Escrow DTO
export interface UpdateEscrowDto {
	notes?: string;
	metadata?: Record<string, any>;
	autoReleaseEnabled?: boolean;
	status?: string;
	withdrawlAmount?: number;
	stripePaymentIntentId?: string;
}

// Query Parameters
export interface EscrowQueryParams {
	page?: number;
	limit?: number;
	filters?: {
		contractId?: string;
		clientId?: string;
		professionalId?: string;
		status?: EscrowStatus[];
		paymentProvider?: EscrowPaymentProvider;
	};
	sort?: Array<{
		orderBy: string;
		order: "ASC" | "DESC";
	}>;
}

// Pagination Response
export interface PaginatedEscrowsResponse {
	data: Escrow[];
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	totalCount: number;
}

// Admin Transactions Response
export interface AdminTransactionsResponse {
	totalVolume: number;
	totalPayments: number;
	totalPlatformFees: number;
	totalCommission: number;
	totalTransactions: number;
	releasedTransactions: number;
	transactions: Escrow[];
	totalCount: number;
	hasNextPage: boolean;
	currency: string;
}

// Payment Processing DTOs
export interface InitiatePaymentResponse {
	escrow: Escrow;
	clientSecret: string;
}

export interface PaymentIntentData {
	clientSecret: string;
	paymentIntentId: string;
}

export interface ReleasePaymentDto {
	releasedBy: string;
}

export interface InitiateDisputeDto {
	reason: DisputeReason;
	description: string;
	initiatedBy: string;
}

export interface ResolveDisputeDto {
	resolution: "release" | "refund";
	resolvedBy: string;
}

export const escrowAPI = {
	// Core CRUD operations
	createEscrow: async (data: EscrowCreateDto): Promise<Escrow> => {
		const response = await HttpService.post("api/v1/escrows", data, headers, true);
		return response;
	},

	// Get professional's current year released amount for commission tier calculation
	getProfessionalCurrentYearReleased: async (
		professionalId: string,
		contractId?: string
	): Promise<{
		professionalId: string;
		contractId?: string;
		currentYearReleasedAmount: number;
		year: number;
		transactionCount: number;
	}> => {
		const url = contractId 
			? `api/v1/escrows/professional/${professionalId}/current-year-released?contractId=${contractId}`
			: `api/v1/escrows/professional/${professionalId}/current-year-released`;
		
		const response = await HttpService.get(
			url,
			true,
			{ "x-custom-lang": "en" }
		);
		return response;
	},

	// Send commission discount notification to professional
	sendCommissionDiscountNotification: async (
		professionalId: string,
		contractAmount: number,
		tierThreshold: number,
		discountPercentage: number,
		tierName: string
	): Promise<{ success: boolean }> => {
		const response = await HttpService.post(
			"api/v1/escrows/commission-discount-notification",
			{
				professionalId,
				contractAmount,
				tierThreshold,
				discountPercentage,
				tierName
			},
			headers,
			true
		);
		return response;
	},

	getEscrows: async (params?: EscrowQueryParams): Promise<PaginatedEscrowsResponse> => {
		let queryString = "";
		if (params) {
			const searchParams = new URLSearchParams();
			if (params.page) searchParams.set("page", params.page.toString());
			if (params.limit) searchParams.set("limit", params.limit.toString());
			if (params.filters) {
				searchParams.set("filters", JSON.stringify(params.filters));
			}
			if (params.sort) {
				searchParams.set("sort", JSON.stringify(params.sort));
			}
			queryString = searchParams.toString();
		}
		const url = queryString ? `api/v1/escrows?${queryString}` : "api/v1/escrows";
		const response = await HttpService.get(url, true, { "x-custom-lang": "en" });
		return response;
	},

	getAdminTransactions: async (params?: EscrowQueryParams): Promise<AdminTransactionsResponse> => {
		let queryString = "";
		if (params) {
			const searchParams = new URLSearchParams();
			if (params.page) searchParams.set("page", params.page.toString());
			if (params.limit) searchParams.set("limit", params.limit.toString());
			if (params.filters) {
				searchParams.set("filters", JSON.stringify(params.filters));
			}
			if (params.sort) {
				searchParams.set("sort", JSON.stringify(params.sort));
			}
			queryString = searchParams.toString();
		}
		const url = queryString ? `api/v1/escrows/admin/transactions?${queryString}` : "api/v1/escrows/admin/transactions";
		const response = await HttpService.get(url, true, { "x-custom-lang": "en" });
		return response;
	},

	getEscrowById: async (id: string): Promise<Escrow> => {
		const response = await HttpService.get(`api/v1/escrows/${id}`, true, { "x-custom-lang": "en" });
		return response;
	},

	updateEscrow: async (id: string, data: UpdateEscrowDto): Promise<Escrow> => {
		const response = await HttpService.patch(`api/v1/escrows/${id}`, data, headers, true);
		return response;
	},

	deleteEscrow: async (id: string): Promise<void> => {
		await HttpService.post(`api/v1/escrows/${id}/delete`, {}, headers, true);
	},

	// Payment processing
	initiatePayment: async (id: string, clientId: string): Promise<InitiatePaymentResponse> => {
		const response = await HttpService.post(
			`api/v1/escrows/${id}/initiate-payment`,
			{ clientId },
			headers,
			true
		);
		return response;
	},

	confirmPayment: async (id: string): Promise<Escrow> => {
		const response = await HttpService.post(`api/v1/escrows/${id}/confirm-payment`, {}, headers, true);
		return response;
	},

	releasePayment: async (id: string, releasedBy: string): Promise<Escrow> => {
		const response = await HttpService.post(`api/v1/escrows/${id}/release-payment`, { releasedBy }, headers, true);
		return response;
	},

	refundPayment: async (id: string): Promise<Escrow> => {
		const response = await HttpService.post(`api/v1/escrows/${id}/refund-payment`, {}, headers, true);
		return response;
	},

	// Dispute management
	initiateDispute: async (id: string, disputeData: InitiateDisputeDto): Promise<Escrow> => {
		const response = await HttpService.post(`api/v1/escrows/${id}/initiate-dispute`, disputeData, headers, true);
		return response;
	},

	resolveDispute: async (id: string, resolution: ResolveDisputeDto): Promise<Escrow> => {
		const response = await HttpService.post(`api/v1/escrows/${id}/resolve-dispute`, resolution, headers, true);
		return response;
	},

	// Query endpoints
	getEscrowByMilestone: async (milestoneId: string): Promise<Escrow | null> => {
		try {
			const response = await HttpService.get(`api/v1/escrows/by-milestone/${milestoneId}`, true, { "x-custom-lang": "en" });
			return response;
		} catch (error) {
			// Return null if no escrow found for this milestone
			return null;
		}
	},

	getEscrowsByContract: async (contractId: string): Promise<Escrow[]> => {
		const response = await HttpService.get(`api/v1/escrows/by-contract/${contractId}`, true, { "x-custom-lang": "en" });
		return response;
	},

	// Legacy methods for backward compatibility
	createMilestoneEscrow: async (contractId: string, milestoneId: string, clientId: string, professionalId: string, amount: number, currencyId?: string, emergencyCost?: number, trackerAmount?: number, paymentMethodId?: string,categoryFormSetting?: CategoryFormSettings): Promise<Escrow> => {
		// Calculate platform fee (2% of total amount including emergency cost and tracker amount)
		const baseAmount = amount + (emergencyCost || 0) + (trackerAmount || 0);
		const platformFee = baseAmount * (categoryFormSetting?.platformFee/100) || 0;
		const totalAmount = baseAmount + platformFee;

		const response = await HttpService.post(
			"api/v1/escrows",
			{
				contractId,
				milestoneId,
				clientId,
				professionalId,
				amount, // Milestone amount (base amount)
				emergencyCost: emergencyCost || 0, // Emergency cost
				trackerAmount: trackerAmount || 0, // Smart Work Tracker amount
				platformFee, // Platform fee amount (2% of total base amount)
				totalAmount, // Total amount including all costs and platform fee
				currency: currencyId || "1", // Send currency ID as "currency" field
				autoReleaseEnabled: false,
				paymentMethodId, // Pass payment method ID for customer lookup
			},
			headers,
			true
		);
		return response;
	},

	// Refund escrow by milestone ID
	refundByMilestone: async (milestoneId: string): Promise<any> => {
		const response = await HttpService.post(`api/v1/escrows/refund-by-milestone/${milestoneId}`, {}, headers, true);
		return response;
	},

	// Legacy method for backward compatibility
	getEscrow: async (escrowId: string): Promise<Escrow> => {
		return escrowAPI.getEscrowById(escrowId);
	},

	// Get user escrow balance and transactions
	getUserEscrowBalance: async (userId: string, params?: {
		page?: number;
		limit?: number;
		filters?: {
			escrowStatus?: EscrowStatus[];
		};
		sort?: Array<{
			orderBy: string;
			order: "ASC" | "DESC";
		}>;
	}): Promise<{
		balance: number;
		escrowHeld: number;
		pendingPayments: number;
		availableBalance: number;
		currency: string;
		transactions: Escrow[];
		totalCount: number;
		hasNextPage: boolean;
	}> => {
		let queryString = "";
		if (params) {
			const searchParams = new URLSearchParams();
			if (params.page) searchParams.set("page", params.page.toString());
			if (params.limit) searchParams.set("limit", params.limit.toString());
			if (params.filters) {
				searchParams.set("filters", JSON.stringify(params.filters));
			}
			if (params.sort) {
				searchParams.set("sort", JSON.stringify(params.sort));
			}
			queryString = searchParams.toString();
		}
		const url = queryString ? `api/v1/escrows/user/${userId}/balance?${queryString}` : `api/v1/escrows/user/${userId}/balance`;
		const response = await HttpService.get(url, true, { "x-custom-lang": "en" });
		return response;
	},

	// Get client escrow balance and transactions (client-specific endpoint)
	getClientEscrowBalance: async (userId: string, params?: {
		page?: number;
		limit?: number;
		filters?: {
			escrowStatus?: EscrowStatus[];
		};
		sort?: Array<{
			orderBy: string;
			order: "ASC" | "DESC";
		}>;
	}): Promise<{
		balance: number; // Deprecated (kept for backwards compatibility)
		escrowHeld: number; // Deprecated (kept for backwards compatibility)
		pendingPayments: number; // Only pending (status 1)
		availableBalance: number; // Only user_funded (status 11)
		totalSpent: number; // Funded (status 2) + Released (status 3)
		currency: string;
		transactions: Escrow[];
		totalCount: number;
		hasNextPage: boolean;
	}> => {
		let queryString = "";
		if (params) {
			const searchParams = new URLSearchParams();
			if (params.page) searchParams.set("page", params.page.toString());
			if (params.limit) searchParams.set("limit", params.limit.toString());
			
			// Handle filters with array notation for escrowStatus
			if (params.filters?.escrowStatus) {
				params.filters.escrowStatus.forEach((status) => {
					searchParams.append("filters[escrowStatus][]", status.toString());
				});
			}
			
			if (params.sort) {
				searchParams.set("sort", JSON.stringify(params.sort));
			}
			queryString = searchParams.toString();
		}
		const url = queryString ? `api/v1/escrows/client/${userId}/balance?${queryString}` : `api/v1/escrows/client/${userId}/balance`;
		const response = await HttpService.get(url, true, { "x-custom-lang": "en" });
		return response;
	},

	// Add funds to wallet using dedicated endpoint
	addFunds: async (data: {
		amount: number;
		currency: string;
		paymentMethodId: string;
		notes?: string;
	}): Promise<any> => {
		const response = await HttpService.post(
			"api/v1/escrows/admin/add-funds",
			data,
			headers,
			true
		);
		return response;
	},

	// Manually release funds from clearing window (Admin only)
	manualReleaseFunds: async (id: string): Promise<Escrow> => {
		const response = await HttpService.post(`api/v1/escrows/${id}/manual-release`, {}, headers, true);
		return response;
	},

	// Hold funds for review (Admin only)
	holdFunds: async (id: string): Promise<Escrow> => {
		const response = await HttpService.post(`api/v1/escrows/${id}/hold-funds`, {}, headers, true);
		return response;
	},

	/**
	 * Helper: Convert amount with currency if needed before payment
	 * @param amount - Amount to convert
	 * @param fromCurrencyId - Source currency ID
	 * @param toCurrencyId - Target currency ID (for payment)
	 * @param currencies - Available currencies from Redux
	 * @returns Object with original and converted amounts
	 */
	convertAmountForPayment: (
		amount: number,
		fromCurrencyId: string,
		toCurrencyId: string,
		currencies: any[]
	): {
		originalAmount: number;
		convertedAmount: number;
		exchangeRate: number;
		originalCurrency: string;
		targetCurrency: string;
		conversionApplied: boolean;
	} => {
		if (fromCurrencyId === toCurrencyId || !currencies || currencies.length === 0) {
			const currency = currencies?.find((c: any) => c.value === fromCurrencyId);
			return {
				originalAmount: amount,
				convertedAmount: amount,
				exchangeRate: 1,
				originalCurrency: currency?.initial || "EUR",
				targetCurrency: currency?.initial || "EUR",
				conversionApplied: false
			};
		}

		const result = convertCurrency(amount, fromCurrencyId, toCurrencyId, currencies);
		return {
			originalAmount: result.originalAmount,
			convertedAmount: result.convertedAmount,
			exchangeRate: result.exchangeRate,
			originalCurrency: result.originalCurrency,
			targetCurrency: result.targetCurrency,
			conversionApplied: true
		};
	},
};
